import React from 'react';
import * as CountryFlags from 'country-flag-icons/react/3x2';
import CURRENCY_ICONS from './currencyIconsSvg';

const ICON_SET = new Set(Object.keys(CURRENCY_ICONS));

export const isFiat = (c) => {
  const lower = (c || '').toLowerCase();
  return ICON_SET.has(lower) && !CRYPTO_SET.has(lower);
};

export const isCrypto = (c) => CRYPTO_SET.has((c || '').toLowerCase());

const CRYPTO_SET = new Set([
  'btc','eth','ltc','sol','doge','bch','xrp','trx','eos','bnb','usdt','usdc','busd',
  'ape','cro','dai','link','sand','shib','uni','pol','trump',
  // Coins without a dedicated SVG icon. Listed here so the fiat-flag fallback below
  // does NOT mistake them for a fiat currency: 'sda' (Sidra chain native coin) would
  // otherwise resolve 'SD' → Sudan's flag. They render as a neutral text badge instead.
  'sda',
]);

// Stake.us sweeps coins — the ACP returns currency codes 'gold' (Gold Coin) and
// 'sweeps' (Stake Cash). No SVG assets exist for them, so they render as tinted
// GC / SC badges instead of the generic unknown-currency fallback.
const US_COINS = {
  gold:   { label: 'GC', cls: 'bg-amber-400/20 text-amber-600 dark:text-amber-400' },
  gc:     { label: 'GC', cls: 'bg-amber-400/20 text-amber-600 dark:text-amber-400' },
  sweeps: { label: 'SC', cls: 'bg-emerald-400/20 text-emerald-600 dark:text-emerald-400' },
  sc:     { label: 'SC', cls: 'bg-emerald-400/20 text-emerald-600 dark:text-emerald-400' },
};

export const isUsCoin = (c) => Boolean(US_COINS[(c || '').toLowerCase()]);

// Tokens with no SVG whose ticker is longer than the badge fits. Without an entry here
// the generic fallback truncates to three characters, and "USDT0" renders as "USD" —
// naming a different, real currency. That is the opposite of what these need to convey:
// USD₮0 is flagged precisely BECAUSE it is not the USDT Stake credits.
const TOKEN_BADGES = {
  usdt0: { label: 'T0', title: 'USD₮0 — LayerZero omnichain Tether (not USDT)' },
};

/**
 * Renders a currency icon (crypto SVG, fiat flag SVG, or text badge).
 * Shows currency code on hover via title tooltip.
 *
 * @param {string} currency - Currency code (e.g. 'btc', 'usd', 'trump')
 * @param {'sm'|'md'|'lg'} size - Icon size: sm=14px, md=16px, lg=20px
 * @param {string} className - Additional class names
 */
const CurrencyIcon = ({ currency, size = 'sm', className = '' }) => {
  const cur = (currency || '').toLowerCase();
  const upper = (currency || '').toUpperCase();
  const sizeClass = size === 'lg' ? 'w-5 h-5' : size === 'md' ? 'w-4 h-4' : 'w-3.5 h-3.5';
  const fontSize = size === 'lg' ? 'text-[9px]' : size === 'md' ? 'text-[8px]' : 'text-[7px]';

  const usCoin = US_COINS[cur];
  if (usCoin) {
    return (
      <span title={usCoin.label === 'GC' ? 'Gold Coin (GC)' : 'Stake Cash (SC)'} className={`inline-flex items-center justify-center ${sizeClass} rounded-full ${usCoin.cls} ${fontSize} font-bold shrink-0 cursor-help ${className}`}>
        {usCoin.label}
      </span>
    );
  }

  const tokenBadge = TOKEN_BADGES[cur];
  if (tokenBadge) {
    return (
      <span title={tokenBadge.title} className={`inline-flex items-center justify-center ${sizeClass} rounded-full bg-gray-200 dark:bg-white/10 ${fontSize} font-bold text-gray-500 dark:text-white/55 shrink-0 cursor-help ${className}`}>
        {tokenBadge.label}
      </span>
    );
  }

  const IconComponent = CURRENCY_ICONS[cur];

  if (IconComponent) {
    const isCryptoIcon = CRYPTO_SET.has(cur);
    return (
      <span title={upper} className={`inline-flex items-center cursor-help shrink-0 ${className}`}>
        <IconComponent className={`${sizeClass} ${isCryptoIcon ? 'rounded-full' : 'rounded-sm'} shrink-0`} />
      </span>
    );
  }

  // Fiat fallback: ACP returns ~170 currencies but only ~55 fiat have hand-extracted
  // SVGs in currencyIconsSvg. For any other fiat, derive the ISO-3166 country code from
  // the ISO-4217 currency code (first two letters — true for almost every fiat, e.g.
  // AFN→AF, GBP→GB, SEK→SE) and render a bundled flag from country-flag-icons (offline,
  // no CDN). Crypto codes are excluded so a 3-letter ticker can't accidentally get a flag.
  if (!CRYPTO_SET.has(cur) && cur.length === 3) {
    const Flag = CountryFlags[cur.slice(0, 2).toUpperCase()];
    if (Flag) {
      return (
        <span title={upper} className={`inline-flex items-center cursor-help shrink-0 ${className}`}>
          <Flag title={upper} className={`${sizeClass} rounded-sm shrink-0`} />
        </span>
      );
    }
  }

  // Unknown currency — show text badge
  return (
    <span title={upper} className={`inline-flex items-center justify-center ${sizeClass} rounded-full bg-gray-200 dark:bg-white/10 ${fontSize} font-bold text-gray-500 dark:text-white/55 shrink-0 cursor-help ${className}`}>
      {upper.length <= 4 ? upper : upper.slice(0, 3)}
    </span>
  );
};

export default CurrencyIcon;
